'use client'

import { useEffect, useRef } from 'react'

export default function GlobeAnimation() {
  const canvasRef = useRef<HTMLCanvasElement>(null)

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d') as CanvasRenderingContext2D
    canvas.width = 500
    canvas.height = 500

    const cx = 250, cy = 250, R = 140

    const assets = [
      { symbol: '₿', label: 'BTC', color: '#F7931A', angle: 0,    speed: 0.007, orbitR: 195, tilt: 0.25, size: 16 },
      { symbol: 'Ξ', label: 'ETH', color: '#8B7CF6', angle: 2.1,  speed: 0.011, orbitR: 178, tilt: -0.45, size: 15 },
      { symbol: '◎', label: 'SOL', color: '#25B362', angle: 4.2,  speed: 0.005, orbitR: 210, tilt: 0.65,  size: 14 },
      { symbol: '◈', label: 'BNB', color: '#F0B90B', angle: 1.0,  speed: 0.014, orbitR: 185, tilt: -0.2,  size: 14 },
      { symbol: '⬡', label: 'AVAX', color: '#E84142', angle: 3.5, speed: 0.009, orbitR: 200, tilt: 0.85,  size: 13 },
    ]

    // Trail history
    const trails: { x: number; y: number; alpha: number }[][] = assets.map(() => [])

    let globeAngle = 0
    let frame: number

    function drawGlobe() {
      // Glow behind globe
      const grad = ctx.createRadialGradient(cx, cy, 0, cx, cy, R * 1.3)
      grad.addColorStop(0, 'rgba(139,124,246,0.08)')
      grad.addColorStop(0.5, 'rgba(37,179,98,0.03)')
      grad.addColorStop(1, 'rgba(0,0,0,0)')
      ctx.beginPath()
      ctx.arc(cx, cy, R * 1.3, 0, Math.PI * 2)
      ctx.fillStyle = grad
      ctx.fill()

      // Latitude lines
      for (let i = 1; i < 8; i++) {
        const lat = (i / 8) * Math.PI
        const y = cy + R * Math.cos(lat)
        const rx = R * Math.sin(lat)
        ctx.beginPath()
        ctx.ellipse(cx, y, rx, rx * 0.15, 0, 0, Math.PI * 2)
        ctx.strokeStyle = 'rgba(255,255,255,0.08)'
        ctx.lineWidth = 0.7
        ctx.stroke()
      }

      // Longitude lines
      for (let i = 0; i < 12; i++) {
        const lon = (i / 12) * Math.PI + globeAngle
        ctx.beginPath()
        let first = true
        for (let t = 0; t <= Math.PI; t += 0.04) {
          const x3d = Math.sin(t) * Math.cos(lon)
          const y3d = -Math.cos(t)
          const z3d = Math.sin(t) * Math.sin(lon)
          const sx = cx + R * x3d
          const sy = cy + R * y3d
          ctx.strokeStyle = `rgba(255,255,255,${z3d > 0 ? 0.15 : 0.05})`
          if (first) { ctx.moveTo(sx, sy); first = false }
          else ctx.lineTo(sx, sy)
        }
        ctx.lineWidth = 0.6
        ctx.stroke()
      }

      // Globe outline with subtle glow
      ctx.beginPath()
      ctx.arc(cx, cy, R, 0, Math.PI * 2)
      ctx.strokeStyle = 'rgba(255,255,255,0.2)'
      ctx.lineWidth = 1
      ctx.stroke()
    }

    function drawAssets() {
      assets.forEach((a, i) => {
        a.angle += a.speed
        const x = cx + a.orbitR * Math.cos(a.angle)
        const y = cy + a.orbitR * Math.sin(a.angle) * Math.sin(a.tilt + 0.4)
        const depth = Math.sin(a.angle)
        const alpha = Math.max(0.25, 0.5 + depth * 0.5)

        // Add to trail
        trails[i].push({ x, y, alpha })
        if (trails[i].length > 18) trails[i].shift()

        // Draw trail
        trails[i].forEach((point, j) => {
          const t = j / trails[i].length
          ctx.beginPath()
          ctx.arc(point.x, point.y, 2 * t, 0, Math.PI * 2)
          ctx.fillStyle = a.color
          ctx.globalAlpha = t * alpha * 0.4
          ctx.fill()
        })
        ctx.globalAlpha = 1

        // Glow circle behind icon
        const glowGrad = ctx.createRadialGradient(x, y, 0, x, y, 18)
        glowGrad.addColorStop(0, a.color + '33')
        glowGrad.addColorStop(1, 'transparent')
        ctx.beginPath()
        ctx.arc(x, y, 18, 0, Math.PI * 2)
        ctx.fillStyle = glowGrad
        ctx.globalAlpha = alpha
        ctx.fill()
        ctx.globalAlpha = 1

        // Outer ring
        ctx.beginPath()
        ctx.arc(x, y, 14, 0, Math.PI * 2)
        ctx.strokeStyle = a.color
        ctx.lineWidth = 0.8
        ctx.globalAlpha = alpha * 0.6
        ctx.stroke()
        ctx.globalAlpha = 1

        // Symbol
        const size = a.size + depth * 3
        ctx.font = `bold ${size}px monospace`
        ctx.fillStyle = a.color
        ctx.globalAlpha = alpha
        ctx.textAlign = 'center'
        ctx.textBaseline = 'middle'
        ctx.fillText(a.symbol, x, y)

        // Ticker label below
        ctx.font = `bold 8px monospace`
        ctx.fillStyle = a.color
        ctx.globalAlpha = alpha * 0.8
        ctx.fillText(a.label, x, y + 22)
        ctx.globalAlpha = 1
      })
    }

    function animate() {
      ctx.clearRect(0, 0, 500, 500)
      drawGlobe()
      drawAssets()
      globeAngle += 0.003
      frame = requestAnimationFrame(animate)
    }

    animate()
    return () => cancelAnimationFrame(frame)
  }, [])

  return <canvas ref={canvasRef} style={{ width: '100%', maxWidth: 480, height: 'auto', aspectRatio: '1' }} />
}